import {
  possibleMade,
  possibleBids,
  INPUT_TYPE_BID,
  INPUT_TYPE_MADE,
} from './constants';
import { migrateStoredValue } from './legacyActualsMigration';
import { reportProblem } from './errorReporting';

import type {
  Round,
  UpdateInputArgs,
  TeamBidsAndMade,
} from '../../types';

export function getMadeErrorText(totalMade: number) {
  if (totalMade === 13) return 'Enter what every player made.';
  const difference = 13 - totalMade;
  const num = Math.abs(difference);
  const moreOrLess = difference >= 0 ? 'more' : 'less';

  return `You need ${num} ${moreOrLess} to continue.`;
}

export function sIfPlural(num: number) {
  return num > 1 ? 's' : '';
}

export function getUnclaimedText(numUnclaimed: number, useTableUI = false) {
  const isSomeoneGettingSet = numUnclaimed < 0;
  if (isSomeoneGettingSet) {
    const numOverbid = Math.abs(numUnclaimed);
    if (useTableUI) {
      return `Overbids: ${numOverbid}`;
    }
    return `${numOverbid} overbid${sIfPlural(
      numOverbid,
    )}! Someone's getting set!`;
  }
  return `Unclaimed: ${numUnclaimed}`;
}

/**
 * The Table board's felt print (C36): "13 UNCLAIMED", or "1 OVERBID". Round
 * history keeps {@link getUnclaimedText} until chat 9 rebuilds it.
 */
export function getFeltPrintUnclaimedText(numUnclaimed: number) {
  return numUnclaimed < 0
    ? `${Math.abs(numUnclaimed)} OVERBID`
    : `${numUnclaimed} UNCLAIMED`;
}

export const getButtonValues = (
  type: typeof INPUT_TYPE_BID | typeof INPUT_TYPE_MADE,
) => {
  if (type === INPUT_TYPE_BID) {
    return possibleBids;
  }
  return possibleMade;
};

// consider renaming
export const updateInput = ({
  input,
  currentRound,
  fieldToUpdate,
}: UpdateInputArgs): Round => {
  const [teamKey, playerKey] = fieldToUpdate.split('.') as [
    'team1BidsAndMade' | 'team2BidsAndMade',
    keyof TeamBidsAndMade,
  ];
  return {
    ...currentRound,
    [teamKey]: {
      ...currentRound[teamKey],
      [playerKey]: input,
    },
  };
};

interface GetEditedRoundHistoryArgs {
  index: number;
  updatedRound: Round;
  roundHistory: Round[];
}

export const getEditedRoundHistory = ({
  index,
  updatedRound,
  roundHistory,
}: GetEditedRoundHistoryArgs) => {
  if (!Array.isArray(roundHistory)) {
    console.warn('Invalid roundHistory passed to getEditedRoundHistory');
    return [updatedRound];
  }
  const clonedRoundHistory = [...roundHistory];
  clonedRoundHistory[index] = updatedRound;
  return clonedRoundHistory;
};

/* 
if there's anything in localStorage, then return that value;
otherwise, set a default value for that key in localStorage
*/
export const defaultLocalStorage = <T>(key: string, value: T): T => {
  try {
    const item = window.localStorage.getItem(key);
    if (item) return migrateStoredValue(key, JSON.parse(item) as T);
  } catch (err) {
    // The saved game is lost: the player starts from the default.
    console.error(`Error in defaultLocalStorage for key ${key}:`, err);
    reportProblem('storage_failed', { storage_operation: 'read' }, err);
    return value;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(`Error in defaultLocalStorage for key ${key}:`, err);
    reportProblem('storage_failed', { storage_operation: 'write' }, err);
  }
  return value;
};

export const getLocalStorage = <T>(key: string): T | null => {
  const item = window.localStorage.getItem(key);
  if (!item) return null;
  return migrateStoredValue(key, JSON.parse(item) as T);
};

/** Throws when the browser refuses the save (storage full, or blocked), so
 *  the reducer keeps the previous state; the failure is reported first. */
export const setLocalStorage = <T>(key: string, value: T) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    reportProblem('storage_failed', { storage_operation: 'write' }, err);
    throw err;
  }
};

export const rotateArr = <T>(arr: T[]): T[] => {
  if (arr.length <= 1) return [...arr];
  return [...arr.slice(1), arr[0]];
};

export function isMadeAutoGenerated(
  fieldToUpdate: string,
  round: Round | null,
  type: string,
): boolean {
  if (type !== INPUT_TYPE_MADE) return false;
  if (!round?.autoGeneratedMade) return false;
  const isTeam1 = fieldToUpdate.includes('team1');
  const isP1 = fieldToUpdate.includes('p1');
  const key =
    `team${isTeam1 ? 1 : 2}P${isP1 ? 1 : 2}` as keyof Round['autoGeneratedMade'];
  return round.autoGeneratedMade[key] === true;
}

export function hasAnyAutoGeneratedMade(round: Round | null): boolean {
  if (!round?.autoGeneratedMade) return false;
  return Object.values(round.autoGeneratedMade).some(
    (value) => value === true,
  );
}
